import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Download } from 'lucide-react';

const HEADINGS = [
  { id: 'change-fatigue-is-not-a-volume-problem', label: 'Change fatigue is not a volume problem' },
  { id: 'the-evidence-and-what-it-does-not-say', label: 'The evidence, and what it does not say' },
  { id: 'the-volume-fallacy', label: 'The volume fallacy' },
  { id: 'the-four-reserves', label: 'The four reserves' },
  { id: 'why-responses-differ-and-what-the-research-shows', label: 'Why responses differ, and what the research shows' },
  { id: 'two-draw-down-profiles', label: 'Two draw-down profiles' },
  { id: 'the-reserve-audit', label: 'The reserve audit' },
  { id: 'four-interventions', label: 'Four interventions' },
  { id: 'three-assumptions-that-make-fatigue-worse', label: 'Three assumptions that make fatigue worse' },
  { id: 'from-conversation-to-measurement', label: 'From conversation to measurement' },
  { id: 'sources-and-notes', label: 'Sources and notes' },
  { id: 'start-with-the-audit', label: 'Start with the audit' },
];

export function WhitepaperChangeFatiguePage() {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    HEADINGS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <main className="pt-32 pb-24 min-h-screen bg-white relative">
      <Helmet>
        <title>Navigating Change Fatigue | FIVE LENS Enneagram</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start mt-8">
          <aside className="hidden lg:block w-72 shrink-0 sticky top-32 max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-hide pr-4">
            <div className="mb-4 text-xs font-bold uppercase tracking-widest text-ink/70">
              Contents
            </div>
            <nav className="flex flex-col gap-3 border-l-2 border-ground-deep">
              {HEADINGS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-left text-sm py-1 pl-4 border-l-2 -ml-[2px] transition-colors ${
                    activeId === id 
                      ? 'border-rule-3 text-ink font-bold' 
                      : 'border-transparent text-ink/70 hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          <article className="flex-1 max-w-3xl min-w-0">
            <div className="mb-8">
              <Breadcrumb items={[
                { label: 'Resource Library', href: '/resource-library' },
                { label: 'Navigating Change Fatigue' }
              ]} />
            </div>
            <div className="prose prose-lg prose-headings:font-sans prose-h1:font-serif prose-h2:text-ink prose-h3:text-ink prose-h4:text-ink prose-a:text-rule-3 text-ink/70 article-content">


<div className="bg-gradient-to-br from-ink to-ink/80 text-white p-12 rounded-lg mb-16 relative overflow-hidden">
  
  <div>
    <div className="mark text-white">FIVE LENS<span className="sub text-white/70">ENNEAGRAM</span></div>
    <div className="tagline text-white/80">Developing People &amp; Expanding Possibilities</div>

    <div className="kicker text-rule-3">Whitepaper</div>
    <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-8">
      <div>
        <h1 className="!text-white !mb-4">Navigating<br />Change Fatigue</h1>
        <div className="deck !mb-0 max-w-2xl text-white/90">
          Actionable strategies for maintaining emotional resilience across enterprise teams
          during large-scale digital transformations and M&amp;A integrations.
        </div>
      </div>
      <a 
        href="/downloads/Navigating-Change-Fatigue-Whitepaper.pdf" 
        download 
        className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rule-3 disabled:pointer-events-none disabled:opacity-50 border border-ground-deep bg-ground/10 hover:bg-ground/20 text-ground shadow-sm h-10 px-4 py-2 shrink-0 no-underline"
      >
        <Download className="mr-2 h-4 w-4" />
        Download PDF
      </a>
    </div>
    <div className="rule5">
      <span style={{"background":"var(--rule-3)"}}></span>
      <span style={{"background":"var(--rule-5)"}}></span>
      <span style={{"background":"var(--rule-3)"}}></span>
      <span style={{"background":"var(--rule-2)"}}></span>
      <span style={{"background":"var(--rule-1)"}}></span>
    </div>
  </div>
  
</div>



<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Executive summary</div>
    <h2 id="change-fatigue-is-not-a-volume-problem">Change fatigue is not a volume problem</h2>
  </div>

  <p className="lead">
    Most organisations diagnose change fatigue as a quantity problem. Too many initiatives, arriving too
    quickly, landing on people who have not recovered from the last one. The volume is real, and it is
    rising. But volume alone does not explain why two people sitting in the same programme, hearing the
    same briefing, absorbing the same disruption, arrive at completely different places.
  </p>

  <p>
    This paper argues that change fatigue is better understood as a distribution problem. The same
    initiative draws down different internal reserves in different people. Generic responses to fatigue,
    more communication, more town halls, more reassurance, address one reserve well and the others hardly
    at all. That is why they reach the people who were already coping and miss the people who are not.
  </p>

  <p>
    We set out a four-part model of what change actually depletes, the published evidence that fatigue
    clusters into distinct patterns rather than a single continuum, a diagnostic that leadership teams can
    run without external support, and four differentiated interventions matched to what has actually been
    drawn down.
  </p>

  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
    <div className="border-t-4 border-t-ink bg-ground-alt/30 p-6 shadow-sm rounded-b-md flex flex-col">
      <div className="text-5xl font-bold text-ink tracking-tight mb-2">43%</div>
      <div className="text-sm leading-relaxed text-ink flex-1">of employees were willing to support enterprise change in 2022, down from 74% in 2016.</div>
      <div className="text-xs text-ink/70 mt-4 uppercase tracking-wider">Gartner, reported in Harvard Business Review, 2023</div>
    </div>
    <div className="border-t-4 border-t-ink bg-ground-alt/30 p-6 shadow-sm rounded-b-md flex flex-col">
      <div className="text-5xl font-bold text-ink tracking-tight mb-2">30%</div>
      <div className="text-sm leading-relaxed text-ink flex-1">of digital transformations met their objectives and created sustainable change.</div>
      <div className="text-xs text-ink/70 mt-4 uppercase tracking-wider">Boston Consulting Group, 2020</div>
    </div>
    <div className="border-t-4 border-t-ink bg-ground-alt/30 p-6 shadow-sm rounded-b-md flex flex-col">
      <div className="text-5xl font-bold text-ink tracking-tight mb-2">33%</div>
      <div className="text-sm leading-relaxed text-ink flex-1">of acquired employees left within the first year, against 12% of comparable hires.</div>
      <div className="text-xs text-ink/70 mt-4 uppercase tracking-wider">Kim, MIT Sloan, US Census matched data</div>
    </div>
  </div>

  <h3>What this paper contains</h3>
  <table className="t-tight"><tbody><tr><td style={{"width":"16mm"}} className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>01</strong></td><td style={{"width":"52mm"}} className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>The evidence</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">What the published research actually shows about change saturation, transformation outcomes and integration attrition.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>02</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>The volume fallacy</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Why slowing down and communicating more produces disappointing results, and what that failure reveals.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>03</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>The four reserves</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Predictability, belonging, standing and meaning. What each one is, and what depletes it.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>04</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Why responses differ</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">The published evidence that people respond to identical change in systematically different ways.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>05</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Two draw-down profiles</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">How a systems transformation and an M&amp;A integration deplete different reserves.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>06</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>The reserve audit</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">A twelve-question diagnostic you can run with a leadership team this week.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>07</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Four interventions</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">What to do once you know which reserve is empty, and what not to do.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>08</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Resilience misread</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Three assumptions about resilience that make fatigue worse.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>09</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Measurement</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Moving from a conversation to something you can track over time.</td></tr>
  </tbody></table></div>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>A note on the evidence in this paper</h4>
    <p style={{"fontSize":"9.2pt"}}>
      Every figure here is attributed to a named source with a date, and the limits of each are stated
      where they matter. Some of the research comes from populations that are not enterprise teams, and we
      say so rather than generalising quietly.
    </p>
  </div>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 01</div>
    <h2 id="the-evidence-and-what-it-does-not-say">The evidence, and what it does not say</h2>
  </div>

  <p className="lead">
    Two things are happening at once. The quantity of change asked of the average employee has risen
    sharply, and their appetite for absorbing it has fallen just as sharply.
  </p>

  <p>
    Gartner research reported in <em>Harvard Business Review</em> in May 2023 found that employees'
    willingness to support enterprise change fell from 74% in 2016 to 43% in 2022. Over the same period,
    the average number of planned enterprise changes affecting a single employee rose from two to ten.
    The demand roughly quintupled while the supply of goodwill nearly halved.
  </p>

  <p>
    That gap shows up in outcomes. Boston Consulting Group studied 70 companies undertaking digital
    transformations alongside a survey of 825 senior executives, and found that only 30% succeeded in
    meeting their objectives and creating sustainable change. A further 44% delivered limited value, and
    26% created almost none.
  </p>

  <p>
    The interesting part of that study is not the headline figure. It is where BCG located the cause.
    Of the six factors they identified as separating success from failure, five are human: an integrated
    strategy with clear goals, leadership commitment from the chief executive through middle management,
    deployment of high-calibre talent, an agile governance mindset, and effective progress monitoring.
    Only one is technological. BCG state the conclusion plainly.
  </p>

  <div className="border-l-4 border-l-ink bg-ground-alt/30 p-6 my-8 text-xl italic text-ink rounded-r-md">
    The technology is important, but the people dimension, organisation, operating model, processes and
    culture, is usually the determining factor.
    <span className="attrib">Boston Consulting Group, Flipping the Odds of Digital Transformation Success, 2020</span>
  </div>

  <h3>Integration carries a measurable people cost</h3>

  <p>
    The M&amp;A evidence is more specific, and more uncomfortable. Research by Daniel Kim at MIT Sloan
    examined roughly 4,000 acquisitions of United States high-technology start-ups between 1990 and 2011,
    covering around 350,000 employees, using matched employer-employee data from the US Census Bureau.
    Within the first year after acquisition, 33% of acquired employees had left, against 12% of comparable
    employees hired through conventional routes. First-year retention ran at 66% for acquired staff and
    88% for everyone else.
  </p>

  <p>
    We flag the boundary of that finding rather than smoothing over it. Kim studied technology start-up
    acquisitions, where the gap between an entrepreneurial environment and a large acquirer is unusually
    wide. The figures should not be read as a general attrition rate for all enterprise M&amp;A. What they
    do establish, on a very large and well-constructed dataset, is that acquisition itself carries an
    independent effect on whether people stay, separate from their skills, their tenure or their pay.
  </p>

  <h3>Where the evidence runs out</h3>

  <p>
    Readers who follow this field will have seen the claim that 70% to 90% of mergers fail. That figure is
    repeated widely and sourced thinly. Even where large advisory firms cite roughly 70%, the underlying
    basis is frequently unstated. We have not used it in this paper. The three figures above are
    attributable, dated and checkable, and they are sufficient to make the case without borrowing
    authority the evidence does not support.
  </p>

  <p>
    Similarly, the phrase "change fatigue" is used far more often than it is measured. Most of the
    published quantitative work on change fatigue as a construct comes from healthcare, particularly
    nursing, rather than from corporate settings. That research is genuinely useful, and Section 04 draws
    on it, but we treat it as evidence about the shape of the phenomenon rather than as a source of
    enterprise benchmarks.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 02</div>
    <h2 id="the-volume-fallacy">The volume fallacy</h2>
  </div>

  <p className="lead">
    When an organisation recognises change fatigue, the response follows a predictable sequence. Slow the
    portfolio down. Sequence initiatives more carefully. Communicate earlier and more often. Hold more
    forums. Repeat the rationale until it lands.
  </p>

  <p>
    None of that is wrong. Portfolio sequencing is sound practice, and communication genuinely helps.
    But leaders who do all of it competently often report the same result: engagement scores move a
    little, the loudest complaints quieten, and a substantial group of people remains flat, disengaged or
    quietly preparing to leave. The intervention worked for some and did almost nothing for others.
  </p>

  <p>
    The reason is visible once the assumption underneath it is made explicit. Slowing down and explaining
    more is a treatment for <strong>uncertainty</strong>. It assumes that what exhausts people is not
    knowing what is coming. For a meaningful proportion of any workforce, that is exactly right, and the
    treatment works.
  </p>

  <p>
    For everyone else it is beside the point. The person whose expertise has just been made irrelevant by
    a new platform is not confused about what is happening. They understand it perfectly. Explaining it
    again, more clearly, with a better slide, does not touch the problem. The person whose team has been
    dissolved into a new structure knows precisely where they now sit. Their difficulty is that they no
    longer recognise the people around them. Another town hall does not help.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>The pattern we see in practice</h4>
    <p>
      Across our work with leadership teams in South Africa, the United Kingdom and the United States,
      the organisations that struggle most with change fatigue are rarely the ones communicating least.
      They are frequently communicating a great deal, with genuine care, and finding that it is not
      landing. That is usually a signal that the communication is addressing a reserve which, for most of
      the affected population, is not the one that is empty.
    </p>
  </div>

  <h3>Fatigue is not the same as resistance</h3>

  <p>
    A second confusion compounds the first. Change fatigue is routinely read as resistance, and resistance
    is treated as something to be overcome through persuasion or pressure. The two are different states
    with different causes.
  </p>

  <p>
    Resistance is a position. It carries energy, it has an argument attached, and it can be engaged with
    directly. Fatigue is a depletion. It has no argument, it does not want a debate, and pressure applied
    to it produces compliance without commitment. The person who tells you at length why the new operating
    model will not work is not fatigued. The person who says "fine, whatever you think is best" and then
    quietly disengages usually is.
  </p>

  <p>
    Mistaking the second for the first is expensive, because the standard treatment for resistance,
    more engagement and more persuasion, is an additional demand on someone who has nothing left to
    spend. It accelerates the very outcome it was meant to prevent.
  </p>

  <div className="border-l-4 border-l-ink bg-ground-alt/30 p-6 my-8 text-xl italic text-ink rounded-r-md">
    Fatigue does not argue with you. That is what makes it so easy to miss, and so costly to leave alone.
  </div>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 03</div>
    <h2 id="the-four-reserves">The four reserves</h2>
  </div>

  <p className="lead">
    If change fatigue is a depletion, the useful question becomes: a depletion of what? In our work the
    answer resolves into four reserves. Each one is a resource people draw on to absorb disruption, each
    is finite, and each is refilled by something different.
  </p>

  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-8">
    <div className="border-t-4 p-6 bg-ground-alt/30 rounded-b-md shadow-sm" style={{"borderTopColor":"var(--rule-3)"}}>
      <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2">Reserve 01</div>
      <h5 className="text-xl font-bold text-ink mb-3">Predictability</h5>
      <div className="text-sm italic text-ink mb-4 font-medium">"Can I anticipate what is coming and plan around it?"</div>
      <div className="text-sm leading-relaxed text-ink/70">Drawn down by ambiguity, shifting timelines, decisions announced late and reversed often.</div>
    </div>
    <div className="border-t-4 p-6 bg-ground-alt/30 rounded-b-md shadow-sm" style={{"borderTopColor":"var(--rule-2)"}}>
      <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2">Reserve 02</div>
      <h5 className="text-xl font-bold text-ink mb-3">Belonging</h5>
      <div className="text-sm italic text-ink mb-4 font-medium">"Do I still know my people, and where I fit among them?"</div>
      <div className="text-sm leading-relaxed text-ink/70">Drawn down by reorganisation, team dissolution, new reporting lines, departures of trusted colleagues.</div>
    </div>
    <div className="border-t-4 p-6 bg-ground-alt/30 rounded-b-md shadow-sm" style={{"borderTopColor":"var(--rule-3)"}}>
      <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2">Reserve 03</div>
      <h5 className="text-xl font-bold text-ink mb-3">Standing</h5>
      <div className="text-sm italic text-ink mb-4 font-medium">"Does what I am good at still count here?"</div>
      <div className="text-sm leading-relaxed text-ink/70">Drawn down by new systems, new methods, new language, and expertise rendered suddenly obsolete.</div>
    </div>
    <div className="border-t-4 p-6 bg-ground-alt/30 rounded-b-md shadow-sm" style={{"borderTopColor":"var(--rule-5)"}}>
      <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2">Reserve 04</div>
      <h5 className="text-xl font-bold text-ink mb-3">Meaning</h5>
      <div className="text-sm italic text-ink mb-4 font-medium">"Does any of this add up to something worth the cost?"</div>
      <div className="text-sm leading-relaxed text-ink/70">Drawn down by initiatives abandoned midway, strategy reversals, and effort that visibly led nowhere.</div>
    </div>
  </div>

  <p style={{"marginTop":"16px"}}>
    These are not personality types, and they are not four kinds of people. Every person holds all four
    reserves. What differs is the depth of each one, and therefore which runs dry first under load. A
    person with deep reserves of standing and shallow reserves of belonging will absorb a demanding
    technical migration with relative ease and be badly affected by a reorganisation that leaves their
    technical work untouched.
  </p>

  <p>
    This is the practical consequence, and it is the argument of this paper in a sentence. Two people can
    experience the same change, describe it accurately in the same terms, and be depleted by completely
    different aspects of it.
  </p>

  <h3>The reserves are not equally visible</h3>

  <p>
    Predictability is the reserve organisations are best equipped to see, because it produces questions.
    People ask what is happening, when, and to whom. Questions are legible, they arrive in forums and
    inboxes, and they can be answered. Communication programmes exist largely because this reserve
    announces itself.
  </p>

  <p>
    The other three are quieter. Depleted belonging produces withdrawal rather than questions. Depleted
    standing produces silence, because admitting that your expertise no longer applies is difficult and
    exposing. Depleted meaning produces a particular kind of professional politeness, full compliance
    with no discretionary effort, which is easily mistaken for acceptance.
  </p>

  <p>
    An organisation that measures change readiness through the questions it receives is therefore
    measuring one reserve out of four, and the one most likely to be already handled.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>Where this model comes from</h4>
    <p>
      The four reserves correspond closely to the Social Drives lens within the FIVE LENS framework, which
      draws on Maslow's work on human motivation and Richard Barrett's values-based levels of
      consciousness. FIVE LENS measures social drives as one of five sequentially stacked lenses, and the
      language in this paper has been kept deliberately non-technical so that a leadership team can use it
      without training. Section 09 covers what changes when the drives are measured rather than discussed.
    </p>
  </div>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 04</div>
    <h2 id="why-responses-differ-and-what-the-research-shows">Why responses differ, and what the research shows</h2>
  </div>

  <p className="lead">
    That people respond differently to identical change is easy to assert and easy to dismiss as obvious.
    It is worth establishing that it has been studied, because the implications are routinely ignored even
    by leaders who would agree with the statement.
  </p>

  <h3>Dispositional differences are measurable and stable</h3>

  <p>
    Shaul Oreg's work, published in the <em>Journal of Applied Psychology</em> in 2003, developed a measure
    of dispositional resistance to change across seven separate studies. It identified four distinct
    dimensions: routine seeking, emotional reaction to imposed change, short-term focus, and cognitive
    rigidity. People scoring higher were less likely to initiate voluntary change, slower to adopt
    innovations, and experienced greater distress when change was imposed on them.
  </p>

  <p>
    Two features matter here. Resistance to change is not one thing; it resolves into separate dimensions
    that can be high in one person and low in another. Someone can react strongly to imposed change while
    remaining cognitively flexible. And these are dispositional characteristics rather than reactions to a
    particular initiative. They arrive with the person.
  </p>

  <h3>Fatigue itself clusters into profiles</h3>

  <p>
    More recently, researchers have applied latent profile analysis to change fatigue directly, a method
    that looks for naturally occurring subgroups rather than assuming a single continuum. Peng and
    colleagues, publishing in <em>BMC Nursing</em> in 2026, surveyed 515 clinical nurses across ten
    hospitals in Sichuan Province, China. Rather than a smooth distribution, they found four distinct
    profiles.
  </p>

  <div className="overflow-x-auto my-8"><table className="min-w-full text-left text-sm whitespace-nowrap"><thead><tr><th style={{"width":"56mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Profile</th><th style={{"width":"20mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Share</th><th className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Character</th></tr></thead><tbody><tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Low fatigue, stable</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">4.9%</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Lowest fatigue scores with minimal fluctuation</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Moderate fatigue, fluctuating</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">8.3%</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Medium-low levels with notable variation</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Moderate fatigue, high craving for stability</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">61.0%</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Upper-middle fatigue, low burnout, strong desire for stability</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>High fatigue, high craving for stability</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">25.8%</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Highest fatigue, low burnout, strong desire for stability</td></tr>
  </tbody></table></div>

  <p>
    Two findings carry directly into enterprise practice. First, fatigue and burnout came apart. The two
    largest groups, 86.8% of the sample, showed substantial fatigue alongside <em>low</em> organisational
    burnout. These are people tired of change but not disengaged from the organisation, precisely the
    population an engagement survey reports as healthy.
  </p>

  <p>
    Second, two factors predicted membership of the low-fatigue group: perceived organisational support
    and self-efficacy. Both are addressable. Neither is a fixed trait of the workforce.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>The limits of this evidence</h4>
    <p>
      This study examined clinical nurses in one Chinese province, and the profile proportions should not
      be transferred to an enterprise workforce. We cite it for its structural finding rather than its
      numbers: when change fatigue is examined with methods capable of detecting subgroups, subgroups are
      what appear. Comparable work has not yet been published in corporate settings, which is itself worth
      noting.
    </p>
  </div>

  <p>
    Both literatures point the same way. People arrive with different dispositions toward change, and
    their fatigue organises into distinguishable patterns rather than a single scale. An intervention
    designed for the average of that distribution fits almost nobody in it.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 05</div>
    <h2 id="two-draw-down-profiles">Two draw-down profiles</h2>
  </div>

  <p className="lead">
    Different kinds of change deplete different reserves. Mapping an initiative before it begins takes
    about an hour, and is one of the highest-return exercises available to a transformation team.
  </p>

  <h3>Large-scale digital transformation</h3>

  <div className="overflow-x-auto my-8"><table className="min-w-full text-left text-sm whitespace-nowrap"><thead><tr><th style={{"width":"34mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Reserve</th><th style={{"width":"26mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Draw-down</th><th className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Why</th></tr></thead><tbody><tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Predictability</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Moderate</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Phased rollouts are usually well communicated. Timelines slip and erode trust in the schedule, but the direction is rarely in doubt.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Belonging</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Low to moderate</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Teams generally stay intact. Some disruption where new delivery structures cut across existing lines.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Standing</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Severe</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">The core hit. Deep expertise in the retiring system becomes worthless overnight. The most experienced people become the least competent in the room, often in front of juniors.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Meaning</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Moderate</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Depends almost entirely on whether previous technology programmes were completed or abandoned.</td></tr>
  </tbody></table></div>

  <p>
    The characteristic failure of these programmes is to treat a standing problem with a predictability
    solution. Training is scheduled, communications increase, roadmaps are published. The people
    struggling are not short of information. They are losing professional identity in public, and roadmap
    clarity does not address that.
  </p>

  <h3>M&amp;A integration</h3>

  <div className="overflow-x-auto my-8"><table className="min-w-full text-left text-sm whitespace-nowrap"><thead><tr><th style={{"width":"34mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Reserve</th><th style={{"width":"26mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Draw-down</th><th className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Why</th></tr></thead><tbody><tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Predictability</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Severe</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Genuine uncertainty about roles, structures and continued employment, often sustained for months while decisions are made elsewhere.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Belonging</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Severe</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Teams are recomposed, trusted colleagues leave, and the acquired population experiences an identity loss the acquirer does not register.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Standing</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Moderate to severe</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Ways of working that were valued in one organisation may carry no currency in the other.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal"><strong>Meaning</strong></td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Moderate to severe</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Particularly acute where people joined for something the acquisition has now ended.</td></tr>
  </tbody></table></div>

  <p>
    M&amp;A draws down all four reserves at once, which is why integration fatigue is qualitatively
    different from transformation fatigue rather than simply worse. It is also why the standard playbook
    produces mixed results. Communication addresses predictability. Retention payments address neither
    belonging nor meaning, and can deepen the meaning problem by making the relationship transactional.
  </p>

  <div className="border-l-4 border-l-ink bg-ground-alt/30 p-6 my-8 text-xl italic text-ink rounded-r-md">
    Kim's finding that a third of acquired employees leave within a year, against 12% of comparable hires,
    is not primarily a compensation problem. People who joined a particular kind of organisation find the
    reason they joined has gone.
    <span className="attrib">On Kim, Predictable Exodus, MIT Sloan</span>
  </div>

  <h3>The asymmetry nobody plans for</h3>

  <p>
    In every integration we have worked with, the acquirer underestimates the asymmetry of the experience.
    For the acquiring organisation the deal is an addition. For the acquired it is a replacement. Those are
    different events, and a single integration communications programme treats them as the same one.
  </p>

  <p>
    The practical consequence is that identical messages, delivered with identical care, are heard in two
    different registers. The table below sets out the pattern we see most often. It is worth reading with a
    specific recent announcement in mind.
  </p>

  <div className="overflow-x-auto my-8"><table className="min-w-full text-left text-sm whitespace-nowrap"><thead><tr><th style={{"width":"44mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">The message sent</th><th style={{"width":"44mm"}} className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Heard by the acquirer</th><th className="pb-4 pt-2 font-bold text-ink border-b-2 border-ink uppercase tracking-wider text-xs">Heard by the acquired</th></tr></thead><tbody><tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">"Nothing changes for now"</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Reassurance. Business as usual.</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">A deferral. Something will change, and we will not be told when.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">"We are adopting best practice from both organisations"</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Fair, and broadly true.</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Ours will be the practices that lose. Standing is at stake, not process.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">"This is an exciting new chapter"</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Accurate description of a growth event.</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">The previous chapter, which many joined for, has been closed by someone else.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">"Retention packages are available for key people"</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">A sensible protective measure.</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">A price has been placed on staying. Those without one learn something about their position.</td></tr>
    <tr><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">"We want to preserve what makes you special"</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">Genuine intent to protect value.</td><td className="py-4 pr-6 border-b border-ground-deep align-top whitespace-normal">The thing being preserved is now a possession of someone else.</td></tr>
  </tbody></table></div>

  <p>
    None of these readings is unreasonable, and none is a failure of communication skill. They follow from
    the fact that the two populations have different reserves under pressure. The acquirer is drawing
    mainly on predictability. The acquired is drawing on all four at once.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>What this changes in practice</h4>
    <p style={{"marginBottom":"0"}}>
      Integration communications are usually built once and delivered twice. The alternative is not two
      different sets of facts, which would be corrosive, but the same facts with different emphasis:
      predictability and sequencing for the acquiring population, and explicit attention to belonging,
      standing and meaning for the acquired one. That single split is, in our experience, the highest-value
      adjustment available to an integration team, and it costs nothing beyond the effort of writing twice.
    </p>
  </div>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 06</div>
    <h2 id="the-reserve-audit">The reserve audit</h2>
  </div>

  <p className="lead">
    This diagnostic is designed to be run by a leadership team without external facilitation. It takes
    about ninety minutes. Its purpose is not precision. It is to stop a team guessing which reserve is
    empty and start them looking.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>How to run it</h4>
    <p style={{"fontSize":"9.2pt"}}>
      <strong>Step 1.</strong> Each leader completes the twelve items below independently, scoring how
      true each statement is <em>of the teams they lead</em>, not of themselves. Use 1 for "not true at
      all" and 5 for "consistently true".<br />
      <strong>Step 2.</strong> Total the three items in each reserve. A reserve scoring 9 or below is
      depleted. A reserve scoring 12 or above is holding.<br />
      <strong>Step 3.</strong> Compare scores across the leadership team before discussing them. Wide
      disagreement on a reserve is itself the finding, and usually means the team is seeing different
      populations.<br />
      <strong>Step 4.</strong> Take the lowest-scoring reserve to Section 07. Do not address more than one
      at a time.
    </p>
  </div>

  <h4 className="mt-8 mb-4 text-lg font-bold" style={{"color":"var(--rule-3)"}}>Predictability</h4>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">People can describe what is expected of them over the next quarter without checking.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">Decisions that affect the team are announced before they are visible through other channels.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">When timelines move, people are told why, and the new date generally holds.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>

  <h4 className="mt-8 mb-4 text-lg font-bold" style={{"color":"var(--rule-2)"}}>Belonging</h4>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">People know who to go to for help, and that person has not changed recently.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">Informal conversation and non-work contact have held up over the past six months.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">Departures are acknowledged openly rather than absorbed in silence.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>

  <h4 className="mt-8 mb-4 text-lg font-bold" style={{"color":"var(--rule-3)"}}>Standing</h4>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">Experienced people are visibly contributing expertise, not only receiving training.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">It is safe here to say openly that you do not yet know how to do something.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">What people were good at before the change is still referred to and valued.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>

  <h4 className="mt-8 mb-4 text-lg font-bold" style={{"color":"var(--rule-5)"}}>Meaning</h4>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">People can explain why this change is happening in their own words, not the official ones.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">The last major initiative was completed, or its ending was explained honestly.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>
  <div className="flex items-start gap-4 py-4 border-b border-ground-deep last:border-b-0"><div className="flex-1 text-sm leading-relaxed">People believe the effort they are putting in will still matter in twelve months.</div><div className="flex gap-2 shrink-0 mt-1"><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i><i className="block w-4 h-4 border border-ink/70/30 rounded-sm bg-white"></i></div></div>

  <p style={{"marginTop":"16px"}} className="footer-note">
    This audit is a structured conversation, not a psychometric instrument. It has not been validated as a
    measure and should not be used for any assessment of individuals. Its value is in directing attention,
    and in surfacing disagreement within a leadership team about what is actually happening.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 07</div>
    <h2 id="four-interventions">Four interventions</h2>
  </div>

  <p className="lead">
    Each reserve is refilled by something specific. The most common error is applying the predictability
    treatment to all four, because it is the one organisations are practised at.
  </p>

  <h3 style={{"color":"var(--rule-3)"}}>Refilling predictability</h3>
  <p><strong>What works:</strong> Shortening the horizon rather than extending it. A confident statement about the next six weeks is worth more than a speculative one about the next year. Naming what has been decided, what is still open, and when the open items will close. Reporting on decisions that were reversed, and why.</p>
  <p><strong>What does not:</strong> More frequent communication that carries no new decisions. Repetition of the vision. Roadmaps beyond the point of genuine confidence, which spend credibility that will be needed later.</p>

  <h3 style={{"color":"var(--rule-2)"}}>Refilling belonging</h3>
  <p><strong>What works:</strong> Protecting existing team structures wherever the change does not strictly require breaking them, which is more often than integration plans assume. Marking departures properly. Giving recomposed teams real work to do together early, because belonging rebuilds through shared task rather than through social events.</p>
  <p><strong>What does not:</strong> Team-building activity detached from the work. Announcing a new culture. Assuming that people who now report to the same manager constitute a team.</p>

  <h3 style={{"color":"var(--rule-3)"}}>Refilling standing</h3>
  <p><strong>What works:</strong> Finding genuine roles for experienced people inside the change rather than positioning them only as recipients of training. Reverse mentoring in both directions, so the exchange is visible. Explicit acknowledgement from senior leaders that expertise is being set aside and that this is a real loss, not a failure of attitude.</p>
  <p><strong>What does not:</strong> Training alone. Enthusiasm about how easy the new system is, which tells a struggling expert that their difficulty is personal. Language about people who "get it" and people who do not.</p>

  <h3 style={{"color":"var(--rule-5)"}}>Refilling meaning</h3>
  <p><strong>What works:</strong> Finishing something. The single most effective intervention available to an organisation with a depleted meaning reserve is to complete one initiative visibly and say so. Honest accounts of why previous programmes stopped. Connecting the current change to an outcome people can observe rather than one reported at board level.</p>
  <p><strong>What does not:</strong> Restating purpose more forcefully. New vision statements, which in a depleted-meaning environment are read as evidence that the previous one was abandoned.</p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>The intervention that works across all four</h4>
    <p>
      Gartner reported in September 2023 that managers creating a psychologically safe environment were
      associated with a 46% reduction in change fatigue. We would treat the figure itself with caution,
      because the sample and method were not published alongside it. The direction is consistent with the
      Peng findings on organisational support, and with what we observe: the reserve most reliably
      refilled at close range is whichever one the person's immediate manager can see. That makes the line
      manager the primary instrument of change resilience.
    </p>
  </div>

  <p>
    Line managers absorb change twice. They experience it themselves, and then they carry it to their
    teams, usually without having been given time to process the first before performing the second.
    Any programme that treats them purely as a communication channel is spending a reserve it has not
    accounted for.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 08</div>
    <h2 id="three-assumptions-that-make-fatigue-worse">Three assumptions that make fatigue worse</h2>
  </div>

  <h3>Assumption one: resilience is a trait some people have</h3>
  <p>
    Treated as a trait, resilience becomes a hiring filter and an implicit judgement. People who are
    struggling are understood to lack something, which makes admitting difficulty a professional risk and
    drives the whole problem underground. The Peng research points the other way: the factors that
    distinguished the low-fatigue group, perceived organisational support and self-efficacy, are both
    responsive to conditions. Self-efficacy in particular, in Bandura's original formulation, is built
    through mastery experience. It is a consequence of circumstances as much as a cause.
  </p>

  <h3>Assumption two: resilience means absorbing more</h3>
  <p>
    Resilience is frequently invoked as a reason to increase load. If the team is resilient, they can take
    another initiative. This inverts the concept. Resilience is the capacity to return to function after
    disruption, and returning requires an interval in which the disruption stops. An organisation that
    responds to demonstrated resilience by removing the interval is consuming the capacity it is praising.
  </p>

  <div className="border-l-4 border-l-ink bg-ground-alt/30 p-6 my-8 text-xl italic text-ink rounded-r-md">
    Resilience is not the ability to absorb unlimited change. It is the capacity to recover between
    changes, which means it depends on there being a between.
  </div>

  <h3>Assumption three: the quiet ones are fine</h3>
  <p>
    The Peng finding deserves emphasis because it contradicts a widely used diagnostic. The two largest
    profiles, together 86.8% of that sample, combined substantial change fatigue with low organisational
    burnout. These people are committed to the organisation and tired of what it keeps asking. On a
    standard engagement survey they will not register as a problem. They will register as loyal.
  </p>
  <p>
    They are, in our experience, the population that leaves without warning eighteen months into a
    transformation, and whose exit interviews say something vague about wanting a new challenge.
  </p>

  <h3>What follows from this</h3>
  <p>
    Change fatigue is not a morale issue to be managed with communication, nor a capability issue to be
    solved with training. It is a resource issue. Reserves are finite, they are drawn down by specific
    features of specific changes, they refill through specific conditions, and they differ from person to
    person in ways that are measurable.
  </p>
  <p>
    Organisations that treat it this way do three things differently. They map the draw-down before the
    initiative starts rather than surveying the damage afterwards. They match the intervention to the
    reserve rather than defaulting to communication. And they treat the recovery interval as part of the
    plan rather than as slack to be recovered when the programme runs late.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">Section 09</div>
    <h2 id="from-conversation-to-measurement">From conversation to measurement</h2>
  </div>

  <p className="lead">
    The reserve audit in Section 06 will take a leadership team a long way. Its limitation is that it
    captures what leaders perceive about their teams, which is a useful signal and not the same thing as
    what those teams are experiencing.
  </p>

  <p>
    Organisations that want to work with this more rigorously eventually need two things the audit cannot
    provide: individual-level insight into which reserves run deep and which run shallow for a given
    person, and a way of tracking movement over the life of a programme rather than at a single point.
  </p>

  <h3>Where the FIVE LENS framework fits</h3>

  <p>
    FIVE LENS is a behavioural psychometric framework rather than a personality typing tool. It measures
    across five sequentially stacked lenses: Personal Mastery, Emotional Resilience, Social Drives, Energy
    Centres, and the Enneagram, the last measured as frequency of use across all nine patterns rather than
    as an assigned type.
  </p>

  <p>
    Two of those lenses bear directly on the material in this paper. <strong>Social Drives</strong>,
    anchored in Maslow's work on motivation and Richard Barrett's levels of consciousness, describes the
    motivational territory the four reserves occupy. <strong>Emotional Resilience</strong> profiles three
    components: self-efficacy, emotional stability and flexibility. Self-efficacy is the component the
    Peng research identified as distinguishing the low-fatigue group.
  </p>

  <div className="border border-ground-deep border-t-4 border-t-ink bg-ground-alt/30 p-6 my-8 rounded-b-md shadow-sm">
    <h4>What we claim, and what we do not</h4>
    <p>
      FIVE LENS is designed and offered for developmental and coaching use. It is not validated for
      selection, and it should not be used to make decisions about hiring, promotion or performance
      rating. It does not predict who will experience change fatigue, and we make no such claim for it.
    </p>
      <p style={{"marginBottom":"0"}}>
      What it does is give an individual, and the coach or facilitator working with them, a structured
      picture of their own motivational drives and resilience profile. In a change context that turns a
      generalised conversation about coping into a specific one about which reserves this particular
      person draws on, and what refills them. Practitioners working with the framework use it to
      differentiate support rather than to categorise people.
    </p>
  </div>

  <p>
    Colin Adam, our chief executive and co-founder, has been co-developing Enneagram and behavioural
    psychometric assessments since the mid-1990s, and has spent more than thirty years coaching senior
    executives through exactly these transitions. His consistent observation, and one this paper is built
    on, is that the most common error in change support is not insufficient care. It is well-intentioned
    support offered in a form the person cannot use.
  </p>

  <h3>A reasonable sequence</h3>
  <ol>
    <li>Run the reserve audit with the leadership team. Establish where the disagreement is.</li>
    <li>Map the current or forthcoming initiative against the draw-down profiles in Section 05.</li>
    <li>Select one reserve. Apply the matched intervention from Section 07 for a full quarter.</li>
    <li>Where individual differentiation is needed, particularly for line managers carrying the change to others, move to individual profiling and coaching.</li>
    <li>Re-run the audit. Movement in a single reserve is a better indicator of progress than an overall engagement score.</li>
  </ol>

  <p className="footer-note" style={{"marginTop":"14px"}}>
    Nothing in this sequence requires an external provider for the first three steps. We have set them out
    in full deliberately. Organisations that work through them and find they need individual-level insight
    will know precisely what they are looking for, which is a better starting point for any conversation
    with us than a general sense that something is wrong.
  </p>
</div>


<div className="mb-16">
  <div className="border-b-2 border-ink pb-4 mb-8">
    <div className="text-xs uppercase tracking-widest text-ink/70 font-bold mb-2 block">References</div>
    <h2 id="sources-and-notes">Sources and notes</h2>
  </div>

  <div className="refs">
    <ol>
      <li>
        O Morain, C. and Aykens, P. (2023). <em>Employees Are Losing Patience with Change Initiatives.</em>
        Harvard Business Review, May 2023. Reports Gartner survey findings on willingness to support
        enterprise change (74% in 2016, 43% in 2022) and average planned enterprise changes per employee
        (two in 2016, ten in 2022). Sample sizes and survey dates are not published in the article.
      </li>
      <li>
        Boston Consulting Group (2020). <em>Flipping the Odds of Digital Transformation Success.</em>
        Based on 70 companies undertaking digital transformations and a survey of 825 senior executives.
        Reports a 30% success rate against stated objectives, with 44% achieving limited value and 26%
        minimal value.
      </li>
      <li>
        Kim, J. D. <em>Predictable Exodus: Startup Acquisitions and Employee Departures.</em> MIT Sloan
        School of Management. Analysis of approximately 4,000 acquisitions of United States high-technology
        start-ups between 1990 and 2011, covering around 350,000 employees, using matched employer-employee
        data from the US Census Bureau. Reports 33% first-year departure among acquired employees against
        12% among comparable conventional hires.
      </li>
      <li>
        Oreg, S. (2003). <em>Resistance to change: Developing an individual differences measure.</em>
        Journal of Applied Psychology, 88(4), 680 to 693. Seven studies establishing four dimensions of
        dispositional resistance to change: routine seeking, emotional reaction to imposed change,
        short-term focus, and cognitive rigidity.
      </li>
      <li>
        Peng, Y., Zhao, Q., He, X., Yang, Y., Wang, L., Ma, L., Lu, R., Xie, C. and Wang, Y. (2026).
        <em>Change fatigue profiles of clinical nurses and their associations with perceived organizational
        support and self-efficacy: A latent profile analysis.</em> BMC Nursing.
        <span style={{"whiteSpace":"nowrap"}}>doi:10.1186/s12912-026-04547-6</span>. Sample of 515 clinical nurses across ten hospitals in Sichuan
        Province, China, surveyed December 2024 to January 2025. Identifies four distinct change fatigue
        profiles.
      </li>
      <li>
        Gartner (2023). <em>Gartner Says HR Leaders can Reduce Employee Fatigue with Proactive Change
        Management.</em> Press release, 12 September 2023. Reports a 46% reduction in change fatigue
        associated with managers creating a psychologically safe environment. Sample size and method are
        not disclosed in the release.
      </li>
      <li>
        Bandura, A. (1997). <em>Self-Efficacy: The Exercise of Control.</em> W. H. Freeman. Referenced for
        the origin of self-efficacy as a construct built through mastery experience.
      </li>
    </ol>

    <h3>A note on figures we have not used</h3>
    <p style={{"fontSize":"9pt"}}>
      The frequently repeated claim that 70% to 90% of mergers and acquisitions fail does not appear in
      this paper. The figure circulates widely, and even where major advisory firms cite a rate in that
      region the underlying basis is generally unstated. We have restricted ourselves to findings we can
      attribute to a named study with a described method.
    </p>

    <h3>About this paper</h3>
    <p style={{"fontSize":"9pt"}}>
      Prepared by FIVE LENS ENNEAGRAM. The FIVE LENS framework is a behavioural psychometric approach used
      by certified practitioners in South Africa, the United Kingdom and the United States. FIVE LENS has
      been in business since 2009 and its practitioner certification carries ICF accreditation.
    </p>
  </div>
</div>



<div className="bg-gradient-to-br from-ink to-ink/80 text-white p-12 rounded-lg mt-16">
    <div className="rule5">
      <span style={{"background":"var(--rule-3)"}}></span>
      <span style={{"background":"var(--rule-5)"}}></span>
      <span style={{"background":"var(--rule-3)"}}></span>
      <span style={{"background":"var(--rule-2)"}}></span>
      <span style={{"background":"var(--rule-1)"}}></span>
    </div>

    <h2 id="start-with-the-audit" className="!text-white">Start with the audit</h2>
    <p style={{"maxWidth":"135mm"}} className="text-white/80">
      Section 06 is designed to be used, not read. If you take one action from this paper, run the reserve
      audit with your leadership team and compare the scores before you discuss them. The disagreement is
      usually more informative than the average.
    </p>

    <div className="cta">
      <h4 className="!text-white">Team Discovery Day</h4>
      <p style={{"maxWidth":"135mm"}} className="text-white/80">
        A facilitated session that takes a leadership team through their own profiles and the dynamics
        between them. Frequently the right starting point where an audit has shown wide disagreement
        about what teams are experiencing.
      </p>
    </div>

    <div className="cta">
      <h4 className="!text-white">Practitioner Certification</h4>
      <p style={{"maxWidth":"135mm"}} className="text-white/80">
        For internal OD, HR and coaching professionals who want to work with the framework directly.
        ICF accredited. Delivered to practitioners across South Africa, the United Kingdom and the
        United States.
      </p>
    </div>

    <div className="cta">
      <h4 className="!text-white">Speak to us</h4>
      <p style={{"maxWidth":"135mm"}} className="text-white/80">
        If you are planning a transformation or integration and want to map the draw-down before it
        begins, a short conversation is usually enough to establish whether we can help.
      </p>
    </div>

    <div className="back-foot text-white/50">
      FIVE LENS ENNEAGRAM &nbsp;·&nbsp; Developing People &amp; Expanding Possibilities<br />
      fivelensenneagram.com &nbsp;·&nbsp; In business since 2009
    </div>
  
          </div>
        </article>
      </div>
    </div>
  </main>
  );
}
